import React from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, ExternalLink, ShieldCheck, Building2, Home, Briefcase,
  HeartPulse, Globe2, Landmark, Wallet, ArrowRight, BookOpen,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";

const CATEGORIES = [
  {
    id: "administratif",
    title: "Administratif & vie quotidienne",
    Icon: Landmark,
    tone: "from-indigo-500 to-blue-500",
    sources: [
      { label: "Service-Public.fr — le portail officiel", url: "https://www.service-public.fr", desc: "Toutes les démarches administratives françaises, ligne d'information juridique." },
      { label: "ANEF (Étrangers en France)", url: "https://administration-etrangers-en-france.interieur.gouv.fr", desc: "Titres de séjour étudiants, VLS-TS, renouvellement en ligne." },
      { label: "France Identité", url: "https://france-identite.gouv.fr", desc: "Carte d'identité numérique et justificatifs officiels." },
      { label: "impots.gouv.fr", url: "https://www.impots.gouv.fr", desc: "Déclaration fiscale étudiante, avis d'imposition, remboursement." },
    ],
  },
  {
    id: "aides",
    title: "Aides financières & bourses",
    Icon: Wallet,
    tone: "from-emerald-500 to-teal-500",
    sources: [
      { label: "CAF — Aides personnelles au logement", url: "https://www.caf.fr", desc: "APL, ALS, ALF, aides ponctuelles, simulateur officiel." },
      { label: "CROUS / MesServices Étudiant", url: "https://www.messervices.etudiant.gouv.fr", desc: "Dossier social étudiant (DSE), bourses sur critères sociaux, aides d'urgence." },
      { label: "1jeune1solution", url: "https://www.1jeune1solution.gouv.fr", desc: "Toutes les aides jeunes centralisées : logement, emploi, mobilité, santé." },
      { label: "Mes Aides — mesdroitssociaux.gouv.fr", url: "https://www.mesdroitssociaux.gouv.fr", desc: "Simulateur multi-organismes (CAF, Pôle Emploi, CPAM, département)." },
    ],
  },
  {
    id: "logement",
    title: "Logement étudiant",
    Icon: Home,
    tone: "from-fuchsia-500 to-pink-500",
    sources: [
      { label: "Trouver un logement — CROUS", url: "https://trouverunlogement.lescrous.fr", desc: "Résidences universitaires officielles, réservation en ligne." },
      { label: "Visale — Garant gratuit Action Logement", url: "https://www.visale.fr", desc: "Caution locative gratuite pour tous les étudiants (18-30 ans)." },
      { label: "DossierFacile", url: "https://www.dossierfacile.fr", desc: "Dossier de location certifié par l'État, accepté par tous les bailleurs." },
      { label: "Locservice / SeLoger — plateformes reconnues", url: "https://www.locservice.fr", desc: "Sites d'annonces vérifiées (moins d'arnaques que le particulier-à-particulier)." },
    ],
  },
  {
    id: "sante",
    title: "Santé & Sécurité sociale",
    Icon: HeartPulse,
    tone: "from-rose-500 to-red-500",
    sources: [
      { label: "Ameli.fr", url: "https://www.ameli.fr", desc: "Sécurité sociale étudiante, remboursements, carte Vitale." },
      { label: "Étudiants étrangers — Sécu", url: "https://etudiant-etranger.ameli.fr", desc: "Inscription obligatoire pour les étudiants internationaux, gratuit." },
      { label: "Nightline / Écoute étudiante 24/7", url: "https://www.nightline.fr", desc: "Écoute anonyme et gratuite (par des étudiants formés)." },
      { label: "Fil Santé Jeunes", url: "https://www.filsantejeunes.com", desc: "0 800 235 236 · anonyme, gratuit, 7j/7." },
    ],
  },
  {
    id: "emploi",
    title: "Emploi, jobs & alternance",
    Icon: Briefcase,
    tone: "from-amber-500 to-orange-500",
    sources: [
      { label: "France Travail (ex-Pôle Emploi)", url: "https://www.francetravail.fr", desc: "Inscription officielle, offres d'emploi, formations." },
      { label: "1jeune1solution — Emploi", url: "https://www.1jeune1solution.gouv.fr/emploi", desc: "Contrats aidés, apprentissage, VIE, service civique." },
      { label: "Alternance.emploi.gouv.fr", url: "https://www.alternance.emploi.gouv.fr", desc: "Portail national de l'alternance, offres vérifiées." },
      { label: "Service civique", url: "https://www.service-civique.gouv.fr", desc: "Mission d'intérêt général indemnisée, 6-12 mois." },
    ],
  },
  {
    id: "international",
    title: "Étudiants internationaux",
    Icon: Globe2,
    tone: "from-cyan-500 to-sky-500",
    sources: [
      { label: "Campus France", url: "https://www.campusfrance.org", desc: "Portail officiel des études supérieures en France pour internationaux." },
      { label: "Études en France (procédure)", url: "https://pastel.diplomatie.gouv.fr/etudesenfrance", desc: "Plateforme unique pour candidater et obtenir son visa étudiant." },
      { label: "France-Visas", url: "https://france-visas.gouv.fr", desc: "Site officiel des visas français (VLS-TS, court séjour...)." },
      { label: "Bourses Eiffel / Erasmus+", url: "https://www.campusfrance.org/fr/programmes-bourses", desc: "Bourses d'excellence françaises et européennes." },
    ],
  },
  {
    id: "juridique",
    title: "Juridique & protection consommateur",
    Icon: ShieldCheck,
    tone: "from-slate-600 to-slate-800",
    sources: [
      { label: "SignalConso — DGCCRF", url: "https://signal.conso.gouv.fr", desc: "Signaler un litige consommateur (arnaques, publicités mensongères)." },
      { label: "Cybermalveillance.gouv.fr", url: "https://www.cybermalveillance.gouv.fr", desc: "Signaler un phishing, une usurpation, un piratage." },
      { label: "CNIL — droits numériques", url: "https://www.cnil.fr", desc: "RGPD, protection des données personnelles, plainte en ligne." },
      { label: "Défenseur des droits", url: "https://www.defenseurdesdroits.fr", desc: "Discrimination, litige avec l'administration, droits fondamentaux." },
    ],
  },
];

export default function SourcesOfficielles() {
  useSeo({
    title: "Sources officielles — Student Hub",
    description: "Toutes les sources officielles françaises utilisées par Student Hub : Service-Public.fr, CAF, CROUS, Ameli, Campus France, ANEF, DGCCRF... Vérifiées et sourcées.",
    canonical: "https://studenthubfrance.fr/sources-officielles",
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sources-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-indigo-500 text-white flex items-center justify-center">
              <GraduationCap size={20} />
            </div>
            <span className="font-display font-bold text-lg text-slate-900">Student Hub</span>
          </Link>
          <Link to="/register" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
            Créer un compte gratuit →
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium mb-4">
            <ShieldCheck size={14} />
            Toutes nos informations sont sourcées auprès de l'État français
          </div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-slate-900 tracking-tight">
            Sources officielles
          </h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Student Hub agrège, résume et rend accessibles les informations publiques françaises destinées aux
            étudiants. Voici <strong>toutes les sources officielles</strong> que nous utilisons pour rédiger nos
            guides, remplir nos simulateurs et vérifier nos aides. Chaque lien renvoie vers l'organisme officiel — jamais
            un intermédiaire.
          </p>
          <p className="mt-3 text-sm text-slate-500 italic">
            Nous ne collectons pas ces données à ta place : Student Hub est un guide, pas un mandataire. Pour
            toute démarche officielle, connecte-toi <em>toujours</em> sur les portails ci-dessous.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-8">
        {CATEGORIES.map((cat) => (
          <div key={cat.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" id={cat.id}>
            <div className={`px-6 py-4 bg-gradient-to-r ${cat.tone} text-white flex items-center gap-3`}>
              <cat.Icon size={22} />
              <h2 className="font-display font-bold text-xl">{cat.title}</h2>
            </div>
            <ul className="divide-y divide-slate-100">
              {cat.sources.map((s) => (
                <li key={s.url} className="p-5 hover:bg-slate-50 transition-colors">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex items-start gap-3 group"
                    data-testid={`source-link-${cat.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-indigo-100 flex items-center justify-center shrink-0 transition-colors">
                      <ExternalLink size={16} className="text-slate-600 group-hover:text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
                        {s.label}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
                      <p className="text-xs text-slate-400 mt-1 font-mono truncate">{s.url}</p>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* Signal fake info CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-2xl bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border border-amber-200 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <BookOpen size={26} className="text-amber-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-display font-bold text-xl text-slate-900">
                Une info obsolète ou une arnaque à signaler ?
              </h3>
              <p className="text-sm text-slate-700 mt-1">
                Aide-nous à garder Student Hub à jour et à protéger les autres étudiants.
              </p>
            </div>
            <Link
              to="/signaler"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors"
              data-testid="cta-signaler"
            >
              Faire un signalement <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Student Hub — Toutes les sources sont publiques et accessibles à tous.</p>
          <p className="mt-2">
            <Link to="/legal/mentions" className="hover:text-slate-700">Mentions légales</Link>
            {" · "}
            <Link to="/securite" className="hover:text-slate-700">Sécurité</Link>
            {" · "}
            <Link to="/blog" className="hover:text-slate-700">Blog & guides</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
